import { DateIntervalType, SubscriptionPlanPriceDiscountType, SubscriptionPlanProductType } from '../api-platform-graphql';

/**
 * Generic adapter interface for adapting a GraphQL SubscriptionPlan to a generic SubscriptionPlanOffer.
 */

/**
 * Decorate GraphQL SubscriptionPlan with additional properties and methods that reduce
 * code duplication across the adapters and subtypes. This base class defines utilities/derived fields shared across each of
 * the existing SubscriptionPlan subtypes.
 */
class DecoratedSubscriptionPlan {
  constructor(subscriptionPlan) {
    this.getPlanPriceOptionDiscount = planPriceOption => {
      const appliedDiscount = this.getAppliedDiscount(planPriceOption);
      if (!appliedDiscount) {
        return undefined;
      }
      return {
        subscriptionDiscountTrial: (planPriceOption === null || planPriceOption === void 0 ? void 0 : planPriceOption.discountTrial) || undefined,
        subscriptionNonDiscountPrice: this.getSubscriptionNonDiscountPrice(planPriceOption),
        subscriptionDiscountInterval: this.getSubscriptionDiscountDuration(planPriceOption),
        subscriptionPlanAppliedDiscount: appliedDiscount,
        subscriptionDiscountPercent: this.getSubscriptionDiscountPercent(planPriceOption),
        subscriptionDiscountPrice: this.getSubscriptionDiscountPrice(planPriceOption),
        subscriptionDiscountMonthlyPrice: this.getSubscriptionDiscountMonthlyPrice(planPriceOption),
        subscriptionNonDiscountMonthlyPrice: this.getSubscriptionNonDiscountMonthlyPrice(planPriceOption)
      };
    };
    this.getSubscriptionDiscountPercent = planPriceOption => {
      const appliedDiscount = this.getAppliedDiscount(planPriceOption);
      if (appliedDiscount && 'type' in appliedDiscount.data && appliedDiscount.data.type === SubscriptionPlanPriceDiscountType.Percentage) {
        return appliedDiscount.data.value;
      }
      return undefined;
    };
    this.getSubscriptionDiscountPrice = planPriceOption => {
      var _planPriceOption$pric;
      return planPriceOption === null || planPriceOption === void 0 || (_planPriceOption$pric = planPriceOption.price) === null || _planPriceOption$pric === void 0 ? void 0 : _planPriceOption$pric.discount;
    };
    this.getSubscriptionDiscountMonthlyPrice = planPriceOption => {
      var _planPriceOption$pric2;
      return planPriceOption === null || planPriceOption === void 0 || (_planPriceOption$pric2 = planPriceOption.price) === null || _planPriceOption$pric2 === void 0 ? void 0 : _planPriceOption$pric2.discountMonthly;
    };
    this.getSubscriptionNonDiscountMonthlyPrice = planPriceOption => {
      var _planPriceOption$pric3;
      return planPriceOption === null || planPriceOption === void 0 || (_planPriceOption$pric3 = planPriceOption.price) === null || _planPriceOption$pric3 === void 0 ? void 0 : _planPriceOption$pric3.monthly;
    };
    this.getSubscriptionNonDiscountPrice = planPriceOption => {
      var _planPriceOption$pric4;
      return planPriceOption === null || planPriceOption === void 0 || (_planPriceOption$pric4 = planPriceOption.price) === null || _planPriceOption$pric4 === void 0 ? void 0 : _planPriceOption$pric4.list;
    };
    this.getSubscriptionDiscountDuration = planPriceOption => {
      const appliedDiscount = this.getAppliedDiscount(planPriceOption);
      if (appliedDiscount && 'type' in appliedDiscount.data) {
        return appliedDiscount.data.interval;
      }
      if (appliedDiscount && 'days' in appliedDiscount.data) {
        const days = appliedDiscount.data.days;
        return {
          type: DateIntervalType.Day,
          duration: days,
          start: 0,
          end: days
        };
      }
      // Shouldn't actually be possible to hit this line based on the current schema, but needed for TS compilation
      return undefined;
    };
    this.subscriptionPlan = subscriptionPlan;
    this.subscriptionPlan = subscriptionPlan;
  }
  getLicenseContext() {
    var _this$subscriptionPla;
    return (_this$subscriptionPla = this.subscriptionPlan) === null || _this$subscriptionPla === void 0 || (_this$subscriptionPla = _this$subscriptionPla.priceOptions[0]) === null || _this$subscriptionPla === void 0 ? void 0 : _this$subscriptionPla.licenseContext;
  }
  getPlanPriceOptions() {
    var _this$subscriptionPla2, _this$subscriptionPla3;
    return (_this$subscriptionPla2 = (_this$subscriptionPla3 = this.subscriptionPlan) === null || _this$subscriptionPla3 === void 0 ? void 0 : _this$subscriptionPla3.priceOptions) !== null && _this$subscriptionPla2 !== void 0 ? _this$subscriptionPla2 : [];
  }
  getPlanPriceOptionIds() {
    return this.getPlanPriceOptions().map(planPriceOption => planPriceOption.id);
  }
  getContentCollectionIds() {
    var _this$subscriptionPla4, _this$subscriptionPla5;
    return (_this$subscriptionPla4 = (_this$subscriptionPla5 = this.subscriptionPlan.contentCollections) === null || _this$subscriptionPla5 === void 0 ? void 0 : _this$subscriptionPla5.map(collection => collection.id)) !== null && _this$subscriptionPla4 !== void 0 ? _this$subscriptionPla4 : [];
  }
  getPriceOption(dateIntervalType) {
    return this.subscriptionPlan.priceOptions.find(planPriceOption => {
      return planPriceOption.renewalInterval.type === dateIntervalType;
    });
  }
  getTrialPeriodDays(planPriceOption) {
    var _planPriceOption$tria, _planPriceOption$tria2;
    return (planPriceOption === null || planPriceOption === void 0 || (_planPriceOption$tria = planPriceOption.trial) === null || _planPriceOption$tria === void 0 ? void 0 : _planPriceOption$tria.dateInterval.type) === DateIntervalType.Day ? planPriceOption === null || planPriceOption === void 0 || (_planPriceOption$tria2 = planPriceOption.trial) === null || _planPriceOption$tria2 === void 0 ? void 0 : _planPriceOption$tria2.dateInterval.count : undefined;
  }
  getAppliedDiscount(planPriceOption) {
    var _planPriceOption$disc;
    return planPriceOption === null || planPriceOption === void 0 || (_planPriceOption$disc = planPriceOption.discount) === null || _planPriceOption$disc === void 0 || (_planPriceOption$disc = _planPriceOption$disc.appliedDiscounts) === null || _planPriceOption$disc === void 0 ? void 0 : _planPriceOption$disc[0];
  }
  get selectedPriceOption() {
    const annualPlanOption = this.getPriceOption(DateIntervalType.Year);
    const monthlyPlanOption = this.getPriceOption(DateIntervalType.Month);
    const monthlyPriceOptionDiscount = this.getPlanPriceOptionDiscount(monthlyPlanOption);
    const annualPriceOptionDiscount = this.getPlanPriceOptionDiscount(annualPlanOption);

    // If we only have a single plan, simply return that plan
    if (!(annualPlanOption && monthlyPlanOption)) {
      return annualPlanOption !== null && annualPlanOption !== void 0 ? annualPlanOption : monthlyPlanOption;
    }

    // Return lowest monthly price option if both price options are discounted
    if (monthlyPriceOptionDiscount && annualPriceOptionDiscount) {
      return annualPriceOptionDiscount.subscriptionDiscountMonthlyPrice < monthlyPriceOptionDiscount.subscriptionDiscountMonthlyPrice ? annualPlanOption : monthlyPlanOption;
    }
    // If there's only one discount available, return the discount option
    // Note: This does not account for the possibility that the annual plan monthly price could be less
    // than the monthly discounted price.
    if (monthlyPriceOptionDiscount || annualPriceOptionDiscount) {
      return annualPriceOptionDiscount ? annualPlanOption : monthlyPlanOption;
    }

    // Return annual plan over monthly if no discounts are available
    return annualPlanOption;
  }
  get selectedDiscount() {
    return this.getPlanPriceOptionDiscount(this.selectedPriceOption);
  }
}

/**
 * Base adapter class to adapt common data that exists for all consumer subscription tiers.
 * Descendants that extend this can add their specific fields while this one handles base data.
 */
class BaseConsumerSubscriptionPlanAdapter {
  adaptBaseSubscriptionPlan(subscriptionPlanOffer, currencyFormatter) {
    var _annualPlanOption$ann, _annualPlanOption$ann2, _defaultPlanOption$pr;
    const subscriptionPlan = new DecoratedSubscriptionPlan(subscriptionPlanOffer);
    const annualPlanOption = subscriptionPlan.getPriceOption(DateIntervalType.Year);
    const monthlyPlanOption = subscriptionPlan.getPriceOption(DateIntervalType.Month);
    const annualSavingAmount = annualPlanOption !== null && annualPlanOption !== void 0 && (_annualPlanOption$ann = annualPlanOption.annualSavings) !== null && _annualPlanOption$ann !== void 0 && _annualPlanOption$ann.amount ? annualPlanOption === null || annualPlanOption === void 0 || (_annualPlanOption$ann2 = annualPlanOption.annualSavings) === null || _annualPlanOption$ann2 === void 0 ? void 0 : _annualPlanOption$ann2.amount : undefined;
    const formattedAnnualSavings = annualSavingAmount ? currencyFormatter.formatCurrency(annualSavingAmount) : undefined;
    const planPriceOptions = subscriptionPlan.getPlanPriceOptions();
    const monthlyPriceOptionDiscount = subscriptionPlan.getPlanPriceOptionDiscount(monthlyPlanOption);
    const annualPriceOptionDiscount = subscriptionPlan.getPlanPriceOptionDiscount(annualPlanOption);
    const selectedDiscount = subscriptionPlan.selectedDiscount;
    const defaultPlanOption = subscriptionPlan.selectedPriceOption;
    const selectedPrice = defaultPlanOption === null || defaultPlanOption === void 0 || (_defaultPlanOption$pr = defaultPlanOption.price) === null || _defaultPlanOption$pr === void 0 ? void 0 : _defaultPlanOption$pr.monthly;
    const formattedMonthlyAmount = currencyFormatter.formatCurrency(selectedPrice);
    const formattedListPrice = currencyFormatter.formatCurrency(defaultPlanOption === null || defaultPlanOption === void 0 ? void 0 : defaultPlanOption.listPrice.amount);
    return {
      annualPlanOption,
      defaultPlanOption,
      isAnnualPlanEnabled: !!annualPlanOption,
      formattedAnnualSavings,
      formattedListPrice,
      formattedMonthlyAmount,
      trialPeriodDays: subscriptionPlan.getTrialPeriodDays(defaultPlanOption),
      monthlyPlanOption,
      planPriceOptionIds: subscriptionPlan.getPlanPriceOptionIds(),
      contentCollectionIds: subscriptionPlan.getContentCollectionIds(),
      priceOption(dateIntervalType) {
        return planPriceOptions.find(planPriceOption => {
          return planPriceOption.renewalInterval.type === dateIntervalType;
        });
      },
      monthlyDiscount: monthlyPriceOptionDiscount,
      annualDiscount: annualPriceOptionDiscount,
      selectedDiscount,
      ...subscriptionPlanOffer
    };
  }
}

/**
 * Adapter for adapting a GraphQL SubscriptionPlan with productType = CONSUMERSUBSCRIPTION to a
 * SubscriptionPlanOffer<ConsumerSubscriptionPlan>. This adapter contains all the business logic to
 * derive additional properties and methods from the GraphQL SubscriptionPlan.
 */
export class ConsumerSubscriptionPlanAdapter extends BaseConsumerSubscriptionPlanAdapter {
  adaptSubscriptionPlan(subscriptionPlanOffer, currencyFormatter) {
    return {
      ...this.adaptBaseSubscriptionPlan(subscriptionPlanOffer, currencyFormatter),
      productType: SubscriptionPlanProductType.Consumersubscription
    };
  }
}

/**
 * Adapter for adapting a GraphQL SubscriptionPlan with productType = CONSUMERSUBSCRIPTION_VALUE_PLAN to a
 * SubscriptionPlanOffer<ConsumerSubscriptionValuePlan>. This adapter contains all the business logic to
 * derive additional properties and methods from the GraphQL SubscriptionPlan.
 */
export class ConsumerSubscriptionValuePlanAdapter extends BaseConsumerSubscriptionPlanAdapter {
  adaptSubscriptionPlan(subscriptionPlanOffer, currencyFormatter) {
    return {
      ...this.adaptBaseSubscriptionPlan(subscriptionPlanOffer, currencyFormatter),
      productType: SubscriptionPlanProductType.ConsumersubscriptionValuePlan
    };
  }
}

/**
 * Adapter for adapting a GraphQL SubscriptionPlan with productType = CONSUMERSUBSCRIPTION_GROW_WITH_GOOGLE_STANDALONE to a
 * SubscriptionPlanOffer<ConsumerSubscriptionGwGStandalonePlan>. This adapter contains all the business logic to
 * derive additional properties and methods from the GraphQL SubscriptionPlan.
 */
export class ConsumerSubscriptionGrowWithGooglePlanAdapter extends BaseConsumerSubscriptionPlanAdapter {
  adaptSubscriptionPlan(subscriptionPlanOffer, currencyFormatter) {
    return {
      ...this.adaptBaseSubscriptionPlan(subscriptionPlanOffer, currencyFormatter),
      productType: SubscriptionPlanProductType.GrowWithGoogle
    };
  }
}

/**
 * Adapter for adapting a GraphQL SubscriptionPlan with productType = CONSUMERSUBSCRIPTION_GROW_WITH_GOOGLE_PERSONAL_PLAN to a
 * SubscriptionPlanOffer<ConsumerSubscriptionGwGPersonalPlan>. This adapter contains all the business logic to
 * derive additional properties and methods from the GraphQL SubscriptionPlan.
 */
export class ConsumerSubscriptionGwGPlusPersonalPlanAdapter extends BaseConsumerSubscriptionPlanAdapter {
  adaptSubscriptionPlan(subscriptionPlan, currencyFormatter) {
    return {
      ...this.adaptBaseSubscriptionPlan(subscriptionPlan, currencyFormatter),
      productType: SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle
    };
  }
}

/**
 * Adapter for adapting a GraphQL SubscriptionPlan with productType = TEAM to a
 * SubscriptionPlanOffer<TeamSubscriptionPlan>. This adapter contains all the business logic to
 * derive additional properties and methods from the GraphQL SubscriptionPlan.
 */
export class TeamPlanAdapter {
  adaptSubscriptionPlan(subscriptionPlanOffer, currencyFormatter) {
    var _annualPlanOption$mon;
    const subscriptionPlan = new DecoratedSubscriptionPlan(subscriptionPlanOffer);
    const contentCollectionIds = subscriptionPlan.getContentCollectionIds();
    const licenseContext = subscriptionPlan.getLicenseContext();
    const annualPlanOption = subscriptionPlan.getPriceOption(DateIntervalType.Year);
    // Current assumption is that monthly plan price per user is the price we want to display
    const monthlyPlanOption = subscriptionPlan.getPriceOption(DateIntervalType.Month);
    const selectedPriceOption = subscriptionPlan.selectedPriceOption;
    const selectedPrice = (_annualPlanOption$mon = annualPlanOption === null || annualPlanOption === void 0 ? void 0 : annualPlanOption.monthlyPrice) !== null && _annualPlanOption$mon !== void 0 ? _annualPlanOption$mon : monthlyPlanOption === null || monthlyPlanOption === void 0 ? void 0 : monthlyPlanOption.listPrice;
    const formattedMonthlyAmount = currencyFormatter.formatCurrency(selectedPrice === null || selectedPrice === void 0 ? void 0 : selectedPrice.amount);
    return {
      contentCollectionIds,
      maximumLicenseCount: licenseContext === null || licenseContext === void 0 ? void 0 : licenseContext.maximumLicenseCount,
      minimumLicenseCount: licenseContext === null || licenseContext === void 0 ? void 0 : licenseContext.minimumLicenseCount,
      licenseContext,
      formattedMonthlyAmount,
      planPriceOptionIds: subscriptionPlan.getPlanPriceOptionIds(),
      trialPeriodDays: subscriptionPlan.getTrialPeriodDays(selectedPriceOption),
      priceOption(dateIntervalType) {
        return subscriptionPlan.getPriceOption(dateIntervalType);
      },
      productType: SubscriptionPlanProductType.Team,
      ...subscriptionPlanOffer
    };
  }
}

/**
 * Factory function for creating a SubscriptionPlanAdapter for a given SubscriptionPlan subtype.
 * @param subscriptionPlanProductType
 */
export function adapterFor(subscriptionPlanProductType) {
  switch (subscriptionPlanProductType) {
    case SubscriptionPlanProductType.Consumersubscription:
      return new ConsumerSubscriptionPlanAdapter();
    case SubscriptionPlanProductType.ConsumersubscriptionValuePlan:
      return new ConsumerSubscriptionValuePlanAdapter();
    case SubscriptionPlanProductType.Team:
      return new TeamPlanAdapter();
    case SubscriptionPlanProductType.GrowWithGoogle:
      return new ConsumerSubscriptionGrowWithGooglePlanAdapter();
    case SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle:
      return new ConsumerSubscriptionGwGPlusPersonalPlanAdapter();
    default:
      throw new Error('Unsupported subscription plan product type');
  }
}
//# sourceMappingURL=subscription-plan-adapters.js.map